/**
 * Color Mode Utilities for WebsiteBuilderV2
 *
 * Provides utilities for sections to resolve colors based on the selected color mode.
 * Supports three modes: white, light, and dark.
 */

import { generateColorPalette } from "./enhancedColorUtils"

/**
 * Color mode constants
 */
export const COLOR_MODES = {
  WHITE: "white",
  LIGHT: "light",
  DARK: "dark"
}

/**
 * Gets the current color mode from section settings
 * Handles both new format (colorMode) and legacy format (showBackgroundColor/backgroundColor)
 *
 * @param {object} settings - Section settings object
 * @returns {string} The color mode: "white", "light", or "dark"
 */
export const getColorMode = (settings = {}) => {
  // New format: colorMode is explicitly set
  if (settings.colorMode) {
    return settings.colorMode
  }

  // Legacy format: convert from showBackgroundColor/backgroundColor
  const showBgColor = settings.showBackgroundColor === true || settings.showBackgroundColor === "true"

  if (showBgColor) {
    if (settings.backgroundColor === "dark") {
      return COLOR_MODES.DARK
    }
    if (settings.backgroundColor === "theme") {
      return COLOR_MODES.LIGHT
    }
  }

  return COLOR_MODES.WHITE
}

/**
 * Generates theme colors with fallback generation for missing colors
 *
 * @param {object} theme - Theme object
 * @returns {object} Theme colors with all required properties
 */
export const getThemeColors = (theme) => {
  if (!theme?.colors) {
    return {
      primary: "#BD654F",
      primaryDark: "#A55A45",
      bgLight: "#F4ECE9",
      bgMedium: "#E6DAD7",
      bgExtraLight: "#F8F6F5",
      bgDark: "#A4786E",
      textDark: "#454A4F",
      textLight: "#FFFFFF",
      secondary: "#E6DAD7",
      stroke: "#E6E6E6"
    }
  }

  const colors = { ...theme.colors }

  // If bgMedium, bgExtraLight, or bgLight are missing, generate them from primary color
  if (!colors.bgMedium || !colors.bgExtraLight || !colors.bgLight) {
    const primaryColor = colors.primary
    if (primaryColor) {
      const generatedPalette = generateColorPalette(primaryColor)
      colors.bgMedium = colors.bgMedium || generatedPalette.bgMedium
      colors.bgExtraLight = colors.bgExtraLight || generatedPalette.bgExtraLight
      colors.bgLight = colors.bgLight || generatedPalette.bgLight
      colors.bgDark = colors.bgDark || generatedPalette.bgDark
    }
  }

  // Ensure textLight exists
  colors.textLight = colors.textLight || "#FFFFFF"
  colors.textDark = colors.textDark || "#454A4F"

  return colors
}

/**
 * Resolves section background color based on color mode
 *
 * @param {object} settings - Section settings
 * @param {object} themeColors - Theme colors (use getThemeColors to generate)
 * @returns {string} Background color hex value
 */
export const resolveSectionBackgroundColor = (settings, themeColors) => {
  const colorMode = getColorMode(settings)

  switch (colorMode) {
    case COLOR_MODES.LIGHT:
      return themeColors.bgLight || "#F4ECE9"
    case COLOR_MODES.DARK:
      return themeColors.primary || "#BD654F"
    case COLOR_MODES.WHITE:
    default:
      return "#FFFFFF"
  }
}

/**
 * Resolves text color based on color mode (for headings and body text)
 *
 * @param {object} settings - Section settings
 * @param {object} themeColors - Theme colors
 * @param {string} type - "heading" or "body"
 * @returns {string} Text color hex value
 */
export const resolveTextColor = (settings, themeColors) => {
  const colorMode = getColorMode(settings)

  // In dark mode, use light text
  if (colorMode === COLOR_MODES.DARK) {
    return themeColors.textLight || "#FFFFFF"
  }

  // In white and light modes, use dark text
  return themeColors.textDark || "#454A4F"
}

/**
 * Resolves card background color based on color mode
 * Cards need to contrast with the section background
 *
 * @param {object} settings - Section settings
 * @param {object} themeColors - Theme colors
 * @returns {string} Card background color hex value
 */
export const resolveCardBackgroundColor = (settings) => {
  const colorMode = getColorMode(settings)

  switch (colorMode) {
    case COLOR_MODES.DARK:
      // In dark mode, cards are white for contrast
      return "#FFFFFF"
    case COLOR_MODES.LIGHT:
      // In light mode, cards are white for slight contrast
      return "#FFFFFF"
    case COLOR_MODES.WHITE:
    default:
      // In white mode, cards are white (or could be bgLight for subtle variation)
      return "#FFFFFF"
  }
}

/**
 * Resolves card text color based on color mode
 *
 * @param {object} settings - Section settings
 * @param {object} themeColors - Theme colors
 * @returns {string} Card text color hex value
 */
export const resolveCardTextColor = (settings, themeColors) =>
  // Cards always have white/light backgrounds, so always use dark text
  themeColors.textDark || "#454A4F"

/**
 * Resolves primary button styles based on color mode
 *
 * @param {object} settings - Section settings
 * @param {object} themeColors - Theme colors
 * @returns {object} Button style properties
 */
export const resolvePrimaryButtonStyles = (settings, themeColors) => {
  const colorMode = getColorMode(settings)

  switch (colorMode) {
    case COLOR_MODES.DARK:
      // In dark mode, use secondary/light button for contrast
      return {
        backgroundColor: themeColors.secondary || themeColors.bgMedium || "#E6DAD7",
        color: themeColors.textDark || "#454A4F",
        hoverBackgroundColor: themeColors.bgLight || "#F4ECE9"
      }
    case COLOR_MODES.LIGHT:
    case COLOR_MODES.WHITE:
    default:
      // In light/white modes, use primary button
      return {
        backgroundColor: themeColors.primary || "#BD654F",
        color: themeColors.textLight || "#FFFFFF",
        hoverBackgroundColor: themeColors.primaryDark || "#A55A45"
      }
  }
}

/**
 * Resolves secondary button styles based on color mode
 *
 * @param {object} settings - Section settings
 * @param {object} themeColors - Theme colors
 * @returns {object} Button style properties
 */
export const resolveSecondaryButtonStyles = (settings, themeColors) => {
  const colorMode = getColorMode(settings)

  switch (colorMode) {
    case COLOR_MODES.DARK:
      // In dark mode, use white outline button
      return {
        backgroundColor: "transparent",
        borderColor: themeColors.textLight || "#FFFFFF",
        color: themeColors.textLight || "#FFFFFF",
        hoverBackgroundColor: "rgba(255, 255, 255, 0.1)"
      }
    case COLOR_MODES.LIGHT:
    case COLOR_MODES.WHITE:
    default:
      // In light/white modes, use secondary color button
      return {
        backgroundColor: themeColors.secondary || themeColors.bgMedium || "#E6DAD7",
        borderColor: "transparent",
        color: themeColors.textDark || "#454A4F",
        hoverBackgroundColor: themeColors.bgLight || "#F4ECE9"
      }
  }
}

/**
 * Resolves link color based on color mode
 *
 * @param {object} settings - Section settings
 * @param {object} themeColors - Theme colors
 * @returns {string} Link color hex value
 */
export const resolveLinkColor = (settings, themeColors) => {
  const colorMode = getColorMode(settings)

  if (colorMode === COLOR_MODES.DARK) {
    // In dark mode, links should be visible - use light color
    return themeColors.textLight || "#FFFFFF"
  }

  // In light/white modes, use primary color for links
  return themeColors.primary || "#BD654F"
}

/**
 * Resolves border/stroke color based on color mode
 *
 * @param {object} settings - Section settings
 * @param {object} themeColors - Theme colors
 * @returns {string} Border color hex value
 */
export const resolveBorderColor = (settings, themeColors) => {
  const colorMode = getColorMode(settings)

  if (colorMode === COLOR_MODES.DARK) {
    // In dark mode, use lighter borders
    return "rgba(255, 255, 255, 0.3)"
  }

  // In light/white modes, use standard stroke color
  return themeColors.stroke || "#E6E6E6"
}

/**
 * Returns a complete color scheme object for a section
 * Useful for getting all colors at once
 *
 * @param {object} settings - Section settings
 * @param {object} theme - Theme object
 * @returns {object} Complete color scheme
 */
export const getSectionColorScheme = (settings, theme) => {
  const themeColors = getThemeColors(theme)
  const colorMode = getColorMode(settings)

  return {
    colorMode,
    sectionBackground: resolveSectionBackgroundColor(settings, themeColors),
    headingColor: resolveTextColor(settings, themeColors, "heading"),
    bodyColor: resolveTextColor(settings, themeColors, "body"),
    cardBackground: resolveCardBackgroundColor(settings, themeColors),
    cardTextColor: resolveCardTextColor(settings, themeColors),
    primaryButton: resolvePrimaryButtonStyles(settings, themeColors),
    secondaryButton: resolveSecondaryButtonStyles(settings, themeColors),
    linkColor: resolveLinkColor(settings, themeColors),
    borderColor: resolveBorderColor(settings, themeColors),
    // Keep original theme colors for reference
    themeColors
  }
}

/**
 * Builds a settings patch for a color mode change, including legacy background fields
 * and typography colors that match the selected mode.
 */
export const getColorModeSettingsPatch = (settings = {}, theme, colorMode) => {
  const patchedSettings = {
    ...settings,
    colorMode,
    showBackgroundColor: colorMode !== COLOR_MODES.WHITE,
    backgroundColor: colorMode === COLOR_MODES.WHITE ? "#ffffff" : colorMode === COLOR_MODES.LIGHT ? "theme" : "dark"
  }
  const colorScheme = getSectionColorScheme(patchedSettings, theme)

  return {
    ...patchedSettings,
    headlineColor: colorScheme.headingColor,
    subheadlineColor: colorScheme.bodyColor
  }
}

/**
 * Checks if the current color mode is dark
 *
 * @param {object} settings - Section settings
 * @returns {boolean} True if dark mode
 */
export const isDarkMode = (settings) => getColorMode(settings) === COLOR_MODES.DARK

/**
 * Checks if the current color mode is light
 *
 * @param {object} settings - Section settings
 * @returns {boolean} True if light mode
 */
export const isLightMode = (settings) => getColorMode(settings) === COLOR_MODES.LIGHT

/**
 * Checks if the current color mode is white
 *
 * @param {object} settings - Section settings
 * @returns {boolean} True if white mode
 */
export const isWhiteMode = (settings) => getColorMode(settings) === COLOR_MODES.WHITE
